import { Alert, AlertDescription, AlertTitle } from "@bcc/components/ui/alert";
import { Button } from "@bcc/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@bcc/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@bcc/components/ui/tabs";
import { useToast } from "@bcc/hooks/use-toast";
import { type ShippingDocumentsConfig, useShippingDocuments } from "@client-shared/hooks/use-shipping-documents";
import { getDefaultLocaleForRegion } from "@client-shared/lib/constants";
import { useConfigState } from "@client-shared/providers/config";
import { useVendorContext } from "@client-shared/providers/vendor";
import type { GroupOption, Locale, PurchaseOrder, ShippingDocumentsTabType } from "@shared/types";
import { Download, Loader2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, type ComponentRef, type SyntheticEvent } from "react";
import { useTranslation } from "react-i18next";
import { SHIPPING_DOCUMENTS_SECTIONS, getLoadingMessageKey, setShippingDocumentsDialogTestId } from "./shipping-documents-dialog.utils";

interface ShippingDocumentsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  purchaseOrders?: PurchaseOrder[];
  manifestPdfUrl?: string;
  title?: string;
  initialTab?: ShippingDocumentsTabType;
  testId?: string;
}

export function ShippingDocumentsDialog({
  open,
  onOpenChange,
  purchaseOrders,
  manifestPdfUrl,
  title,
  initialTab = "combined",
  testId,
}: ShippingDocumentsDialogProps) {
  const { t } = useTranslation();
  const [vendorState] = useVendorContext();
  const [configState, , configUtils] = useConfigState();
  const { toast } = useToast();

  const iframeRef = useRef<ComponentRef<"iframe"> | null>(null);

  const config = useMemo((): ShippingDocumentsConfig => {
    const currentVendor = vendorState?.currentVendor;
    const rawDefaultSize = currentVendor?.properties?.defaultPrintSize;
    const defaultPaperSize =
      rawDefaultSize === "A4" ||
      rawDefaultSize === "A6" ||
      rawDefaultSize === "Letter" ||
      rawDefaultSize === "4x6" ||
      rawDefaultSize === "3x4"
        ? rawDefaultSize
        : undefined;
    const vendorRegion = currentVendor?.vendorKey?.split(":")[0];
    const vendorLocale: Locale = (vendorRegion && getDefaultLocaleForRegion(vendorRegion)) || "en_US";
    const trimmedVendorName = currentVendor?.vendorName?.trim();
    return {
      defaultPaperSize,
      defaultGroupBySettings: currentVendor?.properties?.defaultGroupBySettings,
      vendorLocale,
      vendorName: trimmedVendorName?.length ? trimmedVendorName : undefined,
      sltCustomerAccountNumber: configState?.config?.sltCustomerAccountNumber ?? undefined,
      customerAccountCarrierDetails: vendorState?.customerAccountCarrierDetails,
      translateGrindType: (value) => configUtils?.findTranslatedValue("grind", value),
      onErrorToast: (message) => toast({ title: message, variant: "destructive" }),
      onWarningToast: (warningTitle, description) => toast({ title: warningTitle, description }),
      stampPoNumberOnLabels: currentVendor?.properties?.displayPoNumberOnShippingLabels === true,
    };
  }, [vendorState, configState, configUtils, toast]);

  const {
    activeTab,
    selectedPaperSize: _selectedPaperSize,
    pdfUrl,
    pdfDisplayUrl,
    error,
    isLoading,
    isChangingTab,
    isLargeDataset,
    showPreviewFallback,
    tabs,
    showGroupBy,
    selectedGroupOptions,
    paperSizes,
    handleTabChange,
    handlePaperSizeChange,
    handleGroupOptionToggle,
    handleDownload,
    handleOpenInNewTab,
    handleClose,
    getPaperSizeButtonState,
    getGroupByButtonState,
    handleIframeLoad,
    handleIframeError,
  } = useShippingDocuments({
    open,
    onOpenChange,
    purchaseOrders,
    manifestPdfUrl,
    initialTab,
    config,
  });

  const onIframeLoad = useCallback(
    (event: SyntheticEvent<HTMLIFrameElement>) => {
      handleIframeLoad(event.currentTarget.getAttribute("src") ?? "");
    },
    [handleIframeLoad]
  );

  // Detect blank iframe fallback
  useEffect(() => {
    if (!open || !pdfDisplayUrl || isChangingTab) return;
    const iframe = iframeRef.current;
    if (!iframe) return;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    let hasDetectedFailure = false;
    const checkIfBlank = (): void => {
      if (hasDetectedFailure) return;
      const src = iframe.getAttribute("src") ?? "";
      if (src === "" || src === "about:blank") {
        hasDetectedFailure = true;
        handleIframeError();
      }
    };
    const handleLoad = (): void => {
      timeoutId = setTimeout(checkIfBlank, 500);
    };
    const onIframeError = (): void => {
      hasDetectedFailure = true;
      handleIframeError();
    };
    iframe.addEventListener("load", handleLoad);
    iframe.addEventListener("error", onIframeError);
    const fallbackTimeoutId = setTimeout(checkIfBlank, 2000);
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      clearTimeout(fallbackTimeoutId);
      iframe.removeEventListener("load", handleLoad);
      iframe.removeEventListener("error", onIframeError);
    };
  }, [open, pdfDisplayUrl, isChangingTab, handleIframeError]);

  const createGroupToggleHandler = useCallback((option: GroupOption) => () => handleGroupOptionToggle(option), [handleGroupOptionToggle]);

  const groupByHelperText = useMemo(() => {
    const selected = Array.from(selectedGroupOptions);
    if (selected.length === 0) {
      return t("components.ShippingDocumentsDialog.groupByHelperText.default");
    }
    if (selected.length === 1) {
      return t("components.ShippingDocumentsDialog.groupByHelperText.single", {
        option: t(`components.ShippingDocumentsDialog.${selected[0]}`),
      });
    }
    if (selected.length === 2) {
      return t("components.ShippingDocumentsDialog.groupByHelperText.multiple", {
        first: t(`components.ShippingDocumentsDialog.${selected[0]}`),
        second: t(`components.ShippingDocumentsDialog.${selected[1]}`),
      });
    }
    return t("components.ShippingDocumentsDialog.groupByHelperText.multipleThree", {
      first: t(`components.ShippingDocumentsDialog.${selected[0]}`),
      second: t(`components.ShippingDocumentsDialog.${selected[1]}`),
      third: t(`components.ShippingDocumentsDialog.${selected[2]}`),
    });
  }, [selectedGroupOptions, t]);

  const rootTestId = testId ?? setShippingDocumentsDialogTestId(SHIPPING_DOCUMENTS_SECTIONS.root);
  const areActionsDisabled = !pdfUrl || isChangingTab || isLoading;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="flex h-[85vh] max-h-[85vh] flex-col sm:max-w-[90vw]" testId={rootTestId}>
        <DialogHeader>
          <DialogTitle>{title ?? t("components.ShippingDocumentsDialog.title")}</DialogTitle>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <Tabs
            value={activeTab}
            onValueChange={(value) => void handleTabChange(value as ShippingDocumentsTabType)}
            className="flex min-h-0 flex-1 flex-col"
            testId={setShippingDocumentsDialogTestId(SHIPPING_DOCUMENTS_SECTIONS.tabs)}
          >
            <TabsList>
              {tabs.map((tab) => (
                <TabsTrigger key={tab.id} value={tab.id} disabled={tab.disabled}>
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>

            <div className="mt-4 flex min-h-0 flex-1 gap-4 overflow-hidden">
              {/* Left panel — PDF / loading / error */}
              <div className="relative flex min-w-0 flex-1 flex-col overflow-hidden rounded-md border bg-muted/20">
                {(() => {
                  if (error) {
                    return (
                      <Alert
                        variant="destructive"
                        className="m-4"
                        testId={setShippingDocumentsDialogTestId(SHIPPING_DOCUMENTS_SECTIONS.errorAlert)}
                      >
                        <AlertTitle>{t("components.ShippingDocumentsDialog.errorGeneratingPDF")}</AlertTitle>
                        <AlertDescription>{error}</AlertDescription>
                      </Alert>
                    );
                  }
                  if (pdfDisplayUrl && !isChangingTab) {
                    return (
                      <>
                        {showPreviewFallback && pdfUrl && (
                          <div className="p-4" data-testid={setShippingDocumentsDialogTestId(SHIPPING_DOCUMENTS_SECTIONS.previewFallback)}>
                            <Alert>
                              <AlertDescription className="flex items-center justify-between gap-4">
                                <span>{t("components.ShippingDocumentsDialog.previewFailed")}</span>
                                <Button variant="outline" size="sm" onClick={handleOpenInNewTab}>
                                  {t("components.ShippingDocumentsDialog.openInNewTab")}
                                </Button>
                              </AlertDescription>
                            </Alert>
                          </div>
                        )}
                        <iframe
                          ref={iframeRef}
                          className="h-full w-full"
                          src={pdfDisplayUrl}
                          title={t("components.ShippingDocumentsDialog.pdfPreview")}
                          onLoad={onIframeLoad}
                          data-testid={setShippingDocumentsDialogTestId(SHIPPING_DOCUMENTS_SECTIONS.pdfFrame)}
                        />
                      </>
                    );
                  }
                  return (
                    <div
                      className="flex flex-1 flex-col items-center justify-center gap-3 text-muted-foreground"
                      data-testid={setShippingDocumentsDialogTestId(SHIPPING_DOCUMENTS_SECTIONS.loadingContainer)}
                    >
                      <Loader2 className="h-8 w-8 animate-spin" />
                      <p className="text-sm">{t(getLoadingMessageKey(isLargeDataset, activeTab)) as string}</p>
                    </div>
                  );
                })()}
              </div>

              {/* Right panel — controls + actions */}
              <div className="flex w-52 shrink-0 flex-col gap-6">
                <div className="flex flex-col gap-4">
                  {/* Paper Size */}
                  <div
                    className="flex flex-col gap-2"
                    data-testid={setShippingDocumentsDialogTestId(SHIPPING_DOCUMENTS_SECTIONS.paperSizeGroup)}
                  >
                    <span className="text-sm font-medium">{t("components.ShippingDocumentsDialog.paperSize")}</span>
                    <div className="flex flex-wrap gap-1">
                      {paperSizes.map((size) => {
                        const state = getPaperSizeButtonState(size);
                        return (
                          <Button
                            key={size}
                            variant={state === "pressed" ? "default" : "outline"}
                            size="sm"
                            disabled={state === "loading"}
                            onClick={() => void handlePaperSizeChange(size)}
                          >
                            {size}
                          </Button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Group By */}
                  {showGroupBy && (
                    <div
                      className="flex flex-col gap-2"
                      data-testid={setShippingDocumentsDialogTestId(SHIPPING_DOCUMENTS_SECTIONS.groupByGroup)}
                    >
                      <span className="text-sm font-medium">{t("components.ShippingDocumentsDialog.groupBy")}</span>
                      <div className="flex flex-wrap gap-1">
                        {(["product", "grind", "quantity"] as GroupOption[]).map((option) => {
                          const state = getGroupByButtonState(option);
                          return (
                            <Button
                              key={option}
                              variant={state === "pressed" ? "default" : "outline"}
                              size="sm"
                              onClick={createGroupToggleHandler(option)}
                            >
                              {t(`components.ShippingDocumentsDialog.${option}`)}
                            </Button>
                          );
                        })}
                      </div>
                      {selectedGroupOptions.size > 0 && <p className="text-xs text-muted-foreground">{groupByHelperText}</p>}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="mt-auto flex flex-col gap-2">
                  <Button
                    disabled={areActionsDisabled}
                    onClick={handleDownload}
                    testId={setShippingDocumentsDialogTestId(SHIPPING_DOCUMENTS_SECTIONS.downloadButton)}
                  >
                    <Download className="mr-2 h-4 w-4" />
                    {t("components.ShippingDocumentsDialog.download")}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => onOpenChange(false)}
                    testId={setShippingDocumentsDialogTestId(SHIPPING_DOCUMENTS_SECTIONS.cancelButton)}
                  >
                    {t("components.ShippingDocumentsDialog.cancel")}
                  </Button>
                </div>
              </div>
            </div>
          </Tabs>
        </div>
      </DialogContent>
    </Dialog>
  );
}
